import { Create, Delete, Edit, Warning } from '@mui/icons-material'
import { Box, Button, IconButton, TableCell, Typography } from '@mui/material'
import { runInAction } from 'mobx'
import { observer, useLocalObservable } from 'mobx-react'
import { useCallback, useEffect, useMemo } from 'react'
import { useHistory } from 'react-router'
import {
	BaseSecretModel,
	SecretMetadata,
	VaultRecord,
} from '../../../../api/clients/vaultStorage/DTO'
import SecretsClient from '../../../../api/clients/vaultStorage/SecretsClient'
import VaultStorageClient from '../../../../api/clients/vaultStorage/VaultStorageClient'
import { FullscreenSpinner } from '../../../../components/feedback/circular'
import { TableColumn } from '../../../../components/tables/DTO'
import { FilteredTable } from '../../../../components/tables/FilteredTable'
import {
	FilterControl,
	SelectFilterOption,
} from '../../../../components/tables/filters/DTO'
import { useModals } from '../../../../services/notifications/ModalService'
import { usePrompts } from '../../../../services/notifications/PromptService'
import { toastService } from '../../../../services/notifications/ToastService'
import AdministrationPageContainer from '../AdministrationPageContainer'
import { ConfirmDeleteSecretPrompt } from './ConfirmDeleteSecretPrompt'
import { CreateSecretDialog } from './CreateSecretDialog'

export const VaultStorageDashboard = observer(() => {
	const history = useHistory()
	const modalService = useModals()
	const promptService = usePrompts()

	const localStore = useLocalObservable(() => ({
		secrets: undefined as SecretMetadata[] | undefined,
		vaults: [] as VaultRecord[],
	}))

	const secretsClient = new SecretsClient()

	const getSecrets = async () => {
		const { data } = await secretsClient.getSecretMetadata()

		runInAction(() => {
			localStore.secrets = data
		})
	}

	const getVaults = async () => {
		const vaultsClient = new VaultStorageClient()

		const { data } = await vaultsClient.getVaults()

		runInAction(() => {
			localStore.vaults = data
		})
	}

	useEffect(() => {
		getSecrets()
		getVaults()
	}, [])

	const handleEditSecret = useCallback((secretId: number) => {
		history.push(`_vault-storage/${secretId}`)
	}, [])

	const handleDeleteSecret = async (secretId: number) => {
		try {
			await secretsClient.deleteSecret(secretId)

			toastService.displayToast({
				message: 'Secret deleted',
				area: 'global',
				severity: 'success',
			})

			runInAction(() => {
				localStore.secrets = localStore.secrets?.filter(
					(v) => v.id !== secretId,
				)
			})
		} catch (e) {
			console.error(e)

			toastService.displayToast({
				message: 'Error deleting secret',
				area: 'global',
				severity: 'error',
			})
		}
	}

	const handleCreateSecretClicked = () => {
		modalService
			.showForm((formProps) => (
				<CreateSecretDialog
					vaults={localStore.vaults}
					existingSecretNames={localStore.secrets?.map((v) => v.name) ?? []}
					onConfirm={(v) =>
						formProps.close({
							closeResult: 'okay',
							value: v,
						})
					}
					onCancel={() => formProps.close({ closeResult: 'cancel' })}
				/>
			))
			.then(async (v) => {
				if (v.closeResult === 'cancel') return

				try {
					const { data: newSecret } = await secretsClient.createSecret(
						v.value as BaseSecretModel,
					)

					toastService.displayToast({
						message: 'Secret created',
						area: 'global',
						severity: 'success',
					})

					history.push(`_vault-storage/${newSecret.id}`)
				} catch (e) {
					console.error(e)

					toastService.displayToast({
						message: 'Error creating secret',
						area: 'global',
						severity: 'error',
					})
				}
			})
	}

	const getTypeOptions = useMemo((): SelectFilterOption[] => {
		const typeOptions: Record<string, unknown> = {}
		localStore.secrets?.forEach((s) => (typeOptions[s.vaultType] = true))
		return Object.keys(typeOptions).map((k) => ({
			label: k,
			value: k,
		}))
	}, [localStore.secrets])

	const filterControls: FilterControl[] = useMemo(
		() => [
			{
				type: 'multiselect',
				name: 'vaultType',
				label: 'Type',
				options: getTypeOptions,
			},
			{
				type: 'search',
				name: 'search',
				column: ['name', 'description'],
			},
		],
		[localStore.secrets],
	)

	const columns: TableColumn<SecretMetadata>[] = useMemo(
		() => [
			{
				name: 'name',
				label: 'Name',
				renderContent: (s) => (
					<Typography
						variant="inherit"
						color={s.isMissingPlugin ? 'error' : ''}
					>
						{s.name}
					</Typography>
				),
			},
			{
				name: 'providerType',
				label: 'Type',
				renderContent: (s) =>
					s.isMissingPlugin ? (
						<Box display="flex" alignItems="center" gap={1}>
							<Warning color="error" fontSize="small" />
							<Typography variant="subtitle2" color="error">
								Plugin Missing
							</Typography>
						</Box>
					) : (
						s.vaultType
					),
			},
			{
				name: 'description',
				label: 'Description',
				renderContent: (s) =>
					s.description.length < 100
						? s.description
						: s.description.substring(0, 99) + '...',
			},
			{
				renderHeading: (
					<TableCell key={3} align="right">
						Actions
					</TableCell>
				),
				renderCell: (s) => (
					<TableCell key={3} align="right">
						{!s.isMissingPlugin && (
							<IconButton size="small" onClick={() => handleEditSecret(s.id)}>
								<Edit />
							</IconButton>
						)}
						<IconButton
							size="small"
							onClick={() => {
								promptService
									.showDialog((props) => (
										<ConfirmDeleteSecretPrompt
											secretName={s.name}
											onCancel={() => props.close('cancel')}
											onConfirm={() => props.close('okay')}
										/>
									))
									.then((v) => {
										if (v == 'okay') handleDeleteSecret(s.id)
									})
							}}
						>
							<Delete />
						</IconButton>
					</TableCell>
				),
			},
		],
		[],
	)

	return (
		<AdministrationPageContainer
			title="Vault Storage"
			actions={
				<div>
					<Button
						variant="contained"
						hidden={localStore.vaults.length === 0}
						startIcon={<Create />}
						color="primary"
						onClick={handleCreateSecretClicked}
					>
						Create Secret
					</Button>
				</div>
			}
		>
			{localStore.secrets === undefined ? (
				<FullscreenSpinner />
			) : (
				<FilteredTable
					allEntries={localStore.secrets}
					columns={columns}
					filterControls={filterControls}
				/>
			)}
		</AdministrationPageContainer>
	)
})
